import "./styles.less";

import { Button, Col, Row, Typography } from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import { useContext, useEffect, useRef, useState } from "react";

import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useTemporaryRental } from "./hook";
import { useTheme } from "../../../Styles/ThemeHook";
import { useUser } from "shared-components/Components/User/User.hook";
import { generateUniqueId, getInitialContactForm } from "src/utils/Functions";
import { useContactModal } from "shared-components/Contexts/ContactModal/context";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import { CountryCode, getCountryCallingCode } from "libphonenumber-js";
import { ModalSimilars } from "../ModalSimilars/web";

export const TemporaryRental = ({
	id,
	seasons,
	columns = { xxl: 3, xl: 3, lg: 3, md: 2, sm: 2, xs: 1 },
	rows = 4,
}) => {
	const { country_code } = useContext(ConfigStateContext);
	const { data, sendLead } = useTemporaryRental({ id, seasons });
	const {
		isLoggedIn,
		user: { data: dataUser },
	} = useUser();
	const { theme } = useTheme();
	const { openContactModal, closeContactModal } = useContactModal();
	const screens = useBreakpoint();
	const tableRef = useRef(null);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [activeRows, setActiveRows] = useState(rows);
	const [asked, setAsked] = useState([]);
	const [fieldState, setFieldState] = useState("");
	const [selectedCountryCode, setSelectedCountryCode] = useState({
		code: "",
		value: "",
	});

	const breakpoint = b => {
		if (typeof b.xxl === "undefined") {
			return "xxl";
		}
		if (b.xxl) return "xxl";
		if (b.xl) return "xl";
		if (b.lg) return "lg";
		if (b.md) return "md";
		if (b.sm) return "sm";
		return "xs";
	};

	const itemsToRender = data.filter((o, i) => i < activeRows * columns[breakpoint(screens)]);

	useEffect(() => {
		if (country_code) {
		  	setSelectedCountryCode({
				code: country_code as CountryCode,
				value: `+${getCountryCallingCode(country_code as CountryCode)}`,
			})
		}
	}, [country_code]);

	const askFastAction = async (field = null) => {
		const currentField = field ?? fieldState;

		let res = await sendLead(currentField)
		if (res) {
			setAsked([...asked, field]);
			setShowSuccessModal(true);
		}
	};

	const handleClick = async (e: MouseEvent, field) => {
		e.preventDefault();
		e.stopPropagation();
		const dataModal = getInitialContactForm(isLoggedIn, dataUser);

		let res = await sendLead(field, {
			name: dataModal.name,
			phone: selectedCountryCode.value + dataModal.phone,
			email: dataModal.email,
		});
		
		if (res) {
			setAsked([...asked, field])
			closeContactModal();
			setShowSuccessModal(true);
		}
	};

	const SpanAskFast = ({ field }) => {
		if (asked.some(o => o == field)) {
			return <Typography.Text disabled>{"¡Preguntado!"}</Typography.Text>;
		} else {
			return (
				<button
					onClick={() => {
						setFieldState(field)

						if (!isLoggedIn) {
							openContactModal({
								id: generateUniqueId(),
								owner: null,
								isProject: true,
								projectId: null,
								defaultMessage: "",
								source: "propertyDetail",
								isExpress: true,
								expressCallback: (e) => handleClick(e, field),
								ctaToShow: {
									contact: true,
									phone: false,
									whatsapp: false
								}
							})
						} else {
							askFastAction(field);
						}
					}}
					className="btnQuestion"
				>
					{"¡Pregúntale!"}
				</button>
			);
		}
	};

	const resetRows = () => {
		if (["xs", "sm", "md"].includes(breakpoint(screens))) {
			const offsetHeader = 120;
			const bodyRect = document.body.getBoundingClientRect().top;
			const tableRect = tableRef.current.getBoundingClientRect().top;
			const tablePosition = tableRect - bodyRect;
			const offsetPosition = tablePosition - offsetHeader;
			window.scrollTo(offsetPosition, 0);
		}

		setActiveRows(rows);
	};

	return (
		<>
			<ModalSimilars
				visible={showSuccessModal}
				onCancel={() => setShowSuccessModal(false)}
			>
				<Row justify={"center"} style={{ textAlign: "center" }}>
					<Col span={24}>
						<CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "25px" }} />
					</Col>
					<Col span={24}>
						<Typography.Text strong>
							{"¡Consulta express enviada con éxito!"}
						</Typography.Text>
					</Col>
				</Row>
			</ModalSimilars>

			<div ref={tableRef} className="temporary-rental">
				{itemsToRender.map((season, i) => {
					return (
						<Row
							key={"temporary_rental_" + i}
							gutter={theme.spacing.mdSpacing}
							justify="space-between">
							<Col>
								<Typography.Text ellipsis className="title" title={season.title}>
									<Typography.Text type={"secondary"} className="small">
										᛫
									</Typography.Text>
									{season.title}
								</Typography.Text>
							</Col>
							<Col flex={1} className="dots" />
							<Col>
								{season.value !== 0 ? (
									<Typography.Text strong ellipsis>
										{season.value}
									</Typography.Text>
								) : (
									<SpanAskFast field={season.title} />
								)}
							</Col>
						</Row>
					);
				})}
			</div>

			{itemsToRender.length < data.length && (
				<Button onClick={() => setActiveRows(activeRows * 3)} className="secondary">
					{"Ver más"}
				</Button>
			)}

			{itemsToRender.length === data.length && rows < activeRows && (
				<Button onClick={resetRows} className="secondary">
					{"Ver menos"}
				</Button>
			)}

			<style jsx global>{`
				.temporary-rental {
					grid-template-columns: ${[...Array(columns[breakpoint(screens)])]
					.map(v => "auto")
					.join(" ")};
				}
			`}</style>
		</>
	);
};
